import React, { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase';
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  orderBy, 
  limit,
  startAfter 
} from 'firebase/firestore';
import { MOUNTAIN_AREAS_LIST, ELEVATION_CATEGORIES_LIST } from '../utils/constants';
import {
  getActivityFrequencyLabel,
  getAgeGroupLabel,
  getActivityStyleLabel
} from '../utils/recruitmentLabels';
import './RecruitmentList.css';

const difficultyLevels = {
  beginner: '初心者向け',
  intermediate: '中級者向け',
  advanced: '上級者向け'
};

const participantConditionLabels = {
  anyone: '誰でも歓迎',
  interview: '事前面談あり',
  experienced: '登山経験者のみ',
  women_only: '女性限定',
  men_only: '男性限定'
};

const SEARCH_FETCH_LIMIT = 200;

function normalizeSearchText(value) {
  return String(value || '').toLowerCase().replace(/\s+/g, '');
}

function matchesSearch(recruitment, searchTerm) {
  if (!searchTerm) return true;

  const normalizedTerm = normalizeSearchText(searchTerm);
  const searchTargets = [
    recruitment.mountainName,
    recruitment.route,
    recruitment.eventName,
  ];

  return searchTargets.some((target) => normalizeSearchText(target).includes(normalizedTerm));
}

function getPrefectureSeoName(prefecture) {
  if (!prefecture) return '';
  if (prefecture === '北海道') return prefecture;
  return prefecture.replace(/[都府県]$/, '');
}

function RecruitmentList() {
  const { currentUser } = useAuth();
  const currentUserRef = useRef(currentUser);
  const [searchParams, setSearchParams] = useSearchParams();
  const [recruitments, setRecruitments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [lastVisible, setLastVisible] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [filtersOpen, setFiltersOpen] = useState(false);
  
  // URLから直接値を読み取る（useStateを使わない！）
  const currentPref = searchParams.get('prefecture') || '';
  const currentDiff = searchParams.get('difficulty') || '';
  const currentSearch = searchParams.get('search') || '';
  const currentStatus = searchParams.get('status') || 'active';
  const currentArea = searchParams.get('area') || '';
  const currentElevation = searchParams.get('elevation') || '';
  const currentVerified = searchParams.get('verified') || '';
  const currentType = searchParams.get('type') || '';

  useEffect(() => {
    currentUserRef.current = currentUser;
  }, [currentUser]);

  // 検索実行関数
  const handleSearch = () => {
    setLastVisible(null);
    fetchRecruitments();
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchRecruitments = async (loadMore = false) => {
    setLoading(true);
    setError('');

    try {
      let q = query(collection(db, 'recruitments'), orderBy('createdAt', 'desc'));

      if (currentPref) q = query(q, where('prefecture', '==', currentPref));
      if (currentDiff) q = query(q, where('difficulty', '==', currentDiff));
      if (currentStatus) q = query(q, where('status', '==', currentStatus));
      if (currentArea) q = query(q, where('area', '==', currentArea));
      if (currentElevation) q = query(q, where('elevation', '==', currentElevation));
      if (currentVerified === 'true') q = query(q, where('isVerifiedOrganization', '==', true));
      if (currentType) q = query(q, where('recruitmentType', '==', currentType));

      if (currentSearch) {
        q = query(q, limit(SEARCH_FETCH_LIMIT));
      } else if (loadMore && lastVisible) {
        q = query(q, startAfter(lastVisible), limit(10));
      } else {
        q = query(q, limit(10));
      }

      const querySnapshot = await getDocs(q);

      const blockedIds = new Set();
      if (currentUserRef.current) {
        try {
          const blocksSnap = await getDocs(
            query(collection(db, 'blocks'), where('blockerId', '==', currentUserRef.current.uid))
          );
          blocksSnap.forEach((d) => blockedIds.add(d.data().blockedId));
        } catch (_) {}
      }

      const data = querySnapshot.docs
        .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
        .filter((recruitment) => !blockedIds.has(recruitment.userId))
        .filter((recruitment) => matchesSearch(recruitment, currentSearch));

      setRecruitments((prev) => (loadMore && !currentSearch ? [...prev, ...data] : data));
      setLastVisible(currentSearch ? null : (querySnapshot.docs[querySnapshot.docs.length - 1] || null));
      setHasMore(currentSearch ? false : querySnapshot.docs.length === 10);
    } catch (err) {
      console.error('????????????????????:', err);
      setError('fetch-failed');
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchRecruitments();
  }, [currentPref, currentDiff, currentSearch, currentStatus, currentArea, currentElevation, currentVerified, currentType, currentUser, lastVisible]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFilterChange = (key, value) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) newParams.set(key, value); else newParams.delete(key);
    setSearchParams(newParams); // これだけでuseEffectが動く
  };

  const seoPrefecture = getPrefectureSeoName(currentPref);
  const pageTitle = seoPrefecture
    ? `登山仲間募集　${seoPrefecture}`
    : '登山仲間募集一覧';

  return (
    <div className="recruitment-list-container">
      <div className="list-header">
        <h1>{pageTitle}</h1>
        {currentPref && (
          <p className="pref-seo-desc">
            {seoPrefecture}で登山メンバーを募集・応募できます。日帰り登山から縦走まで、
            {seoPrefecture}の登山仲間を「ヤマッチ」で探しましょう。
          </p>
        )}
        {currentUser && (
          <Link to="/create-recruitment" className="btn btn-primary">
            新規募集
          </Link>
        )}
      </div>

      <div className={`filters-section${filtersOpen ? '' : ' filters-collapsed'}`}>
        <button
          className="filters-toggle"
          onClick={() => setFiltersOpen(o => !o)}
          aria-expanded={filtersOpen}
        >
          {filtersOpen ? '▲ フィルターを閉じる' : '▼ フィルターで絞り込む'}
        </button>
        <div className="filter-row">
          <div className="form-group">
            <label className="form-label">都道府県（集合場所）</label>
            <select
              className="form-control"
              value={currentPref}
              onChange={(e) => handleFilterChange('prefecture', e.target.value)}
            >
              <option value="">すべて</option>
              <option value="北海道">北海道</option>
              <option value="青森県">青森県</option>
              <option value="岩手県">岩手県</option>
              <option value="宮城県">宮城県</option>
              <option value="秋田県">秋田県</option>
              <option value="山形県">山形県</option>
              <option value="福島県">福島県</option>
              <option value="茨城県">茨城県</option>
              <option value="栃木県">栃木県</option>
              <option value="群馬県">群馬県</option>
              <option value="埼玉県">埼玉県</option>
              <option value="千葉県">千葉県</option>
              <option value="東京都">東京都</option>
              <option value="神奈川県">神奈川県</option>
              <option value="山梨県">山梨県</option>
              <option value="新潟県">新潟県</option>
              <option value="富山県">富山県</option>
              <option value="石川県">石川県</option>
              <option value="福井県">福井県</option>
              <option value="長野県">長野県</option>
              <option value="岐阜県">岐阜県</option>
              <option value="静岡県">静岡県</option>
              <option value="愛知県">愛知県</option>
              <option value="三重県">三重県</option>
              <option value="滋賀県">滋賀県</option>
              <option value="京都府">京都府</option>
              <option value="大阪府">大阪府</option>
              <option value="兵庫県">兵庫県</option>
              <option value="奈良県">奈良県</option>
              <option value="和歌山県">和歌山県</option>
              <option value="鳥取県">鳥取県</option>
              <option value="島根県">島根県</option>
              <option value="岡山県">岡山県</option>
              <option value="広島県">広島県</option>
              <option value="山口県">山口県</option>
              <option value="徳島県">徳島県</option>
              <option value="香川県">香川県</option>
              <option value="愛媛県">愛媛県</option>
              <option value="高知県">高知県</option>
              <option value="福岡県">福岡県</option>
              <option value="佐賀県">佐賀県</option>
              <option value="長崎県">長崎県</option>
              <option value="熊本県">熊本県</option>
              <option value="大分県">大分県</option>
              <option value="宮崎県">宮崎県</option>
              <option value="鹿児島県">鹿児島県</option>
              <option value="沖縄県">沖縄県</option>
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">登山エリア</label>
            <select
              className="form-control"
              value={currentArea}
              onChange={(e) => handleFilterChange('area', e.target.value)}
            >
              <option value="">すべて</option>
              {MOUNTAIN_AREAS_LIST.map(area => (
                <option key={area.id} value={area.id}>
                  {area.name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">標高区分</label>
            <select
              className="form-control"
              value={currentElevation}
              onChange={(e) => handleFilterChange('elevation', e.target.value)}
            >
              <option value="">すべて</option>
              {ELEVATION_CATEGORIES_LIST.map(category => (
                <option key={category.id} value={category.id}>
                  {category.name} ({category.range.min}m〜{category.range.max}m)
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">難易度</label>
            <select
              className="form-control"
              value={currentDiff}
              onChange={(e) => handleFilterChange('difficulty', e.target.value)}
            >
              <option value="">すべて</option>
              <option value="beginner">初心者向け</option>
              <option value="intermediate">中級者向け</option>
              <option value="advanced">上級者向け</option>
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">募集タイプ</label>
            <select
              className="form-control"
              value={currentType}
              onChange={(e) => handleFilterChange('type', e.target.value)}
            >
              <option value="">すべて</option>
              <option value="single">単発募集</option>
              <option value="group">所属メンバー募集</option>
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">募集ステータス</label>
            <select
              className="form-control"
              value={currentStatus}
              onChange={(e) => handleFilterChange('status', e.target.value)}
            >
              <option value="active">募集中</option>
              <option value="closed">募集終了</option>
              <option value="cancelled">募集中止</option>
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">
              <input
                type="checkbox"
                checked={currentVerified === 'true'}
                onChange={(e) => handleFilterChange('verified', e.target.checked ? 'true' : '')}
                style={{marginRight: '8px'}}
              />
              優良認定サークルのみ表示
            </label>
          </div>
        </div>

        <div className="search-row">
          <div className="form-group search-input">
            <input
              type="text"
              className="form-control"
              placeholder="山名やキーワードで検索"
              value={currentSearch}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleSearch();
                }
              }}
            />
            <button 
              type="button" 
              className="btn btn-primary search-btn"
              onClick={handleSearch}
            >
              検索
            </button>
          </div>
        </div>
      </div>

      {error === 'fetch-failed' ? (
        <div className="no-results">
          <p>募集の取得に失敗しました。時間をおいて再度お試しください。</p>
        </div>
      ) : loading && !recruitments.length ? (
        <div className="loading">読み込み中...</div>
      ) : (
        <div className="recruitments-list">
          {recruitments.length === 0 ? (
            <div className="no-results">
              <p>該当する募集が見つかりませんでした。</p>
              <p>検索条件を変えてお試しください。</p>
            </div>
          ) : (
            <>
              {recruitments.map((recruitment) => {
                const isGroup = recruitment.recruitmentType === 'group';
                const title = isGroup ? recruitment.groupName : recruitment.mountainName;
                return (
                <div key={recruitment.id} className="recruitment-card">
                  <div className="card-header">
                    <h3>{title}</h3>
                    {!isGroup && recruitment.difficulty && (
                      <span className={`difficulty ${recruitment.difficulty}`}>
                        {difficultyLevels[recruitment.difficulty]}
                      </span>
                    )}
                    {isGroup && <span className="badge" style={{background:'var(--forest, #2d6a4f)', color:'#fff'}}>所属メンバー募集</span>}
                    {recruitment.isVerifiedOrganization && (
                      <span className="badge verified-badge">優良認定サークル</span>
                    )}
                  </div>
                  <div className="card-body">
                    {!isGroup && recruitment.route && <p className="route">ルート: {recruitment.route}</p>}
                    {!isGroup && recruitment.participantCondition && (
                      <p className="route">参加条件: {participantConditionLabels[recruitment.participantCondition] || recruitment.participantCondition}</p>
                    )}
                    {!isGroup && recruitment.departureDate && (
                      <p className="date">
                        📅 山行日: {recruitment.departureDate?.toDate ? recruitment.departureDate.toDate().toLocaleDateString() : new Date(recruitment.departureDate).toLocaleDateString()}
                      </p>
                    )}
                    {isGroup && recruitment.activityFrequency && (
                      <p className="route">
                        活動頻度: {getActivityFrequencyLabel(recruitment.activityFrequency, recruitment.activityFrequencyOther)}
                      </p>
                    )}
                    {isGroup && recruitment.genderRatio && (
                      <p className="route">男女比: {recruitment.genderRatio}</p>
                    )}
                    {isGroup && recruitment.ageGroup && (
                      <p className="route">年齢層: {getAgeGroupLabel(recruitment.ageGroup)}</p>
                    )}
                    {isGroup && recruitment.activityStyle && (
                      <p className="route">活動スタイル: {getActivityStyleLabel(recruitment.activityStyle)}</p>
                    )}
                    {isGroup ? (
                      <p className="participants">👥 応募数: {recruitment.applicationCount}件</p>
                    ) : recruitment.maxParticipants > 0 ? (
                      <p className="participants">👥 {recruitment.applicationCount}/{recruitment.maxParticipants}人</p>
                    ) : (
                      <p className="participants">👥 応募数: {recruitment.applicationCount}件</p>
                    )}
                    <p className="prefecture">
                      📍 {recruitment.prefecture}
                    </p>
                    {!isGroup && recruitment.meetingPoint && (
                      <p className="meeting-point">
                        🕐 {recruitment.meetingPoint}
                      </p>
                    )}
                    <p className="status">
                      📋 {recruitment.status === 'active' ? '募集中' : recruitment.status === 'closed' ? '募集終了' : '募集中止'}
                    </p>
                  </div>
                  <div className="card-footer">
                    <Link to={`/recruitments/${recruitment.id}`} className="btn btn-primary">
                      詳細を見る
                    </Link>
                  </div>
                </div>
                );
              })}
              
              {hasMore && (
                <div className="load-more">
                  <button 
                    className="btn btn-secondary"
                    onClick={() => fetchRecruitments(true)}
                    disabled={loading}
                  >
                    {loading ? '読み込み中...' : 'もっと見る'}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default RecruitmentList;
